import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useNavigate } from 'react-router-dom';
import { validatePassword, isPasswordValid } from '@/utils/passwordUtils';
import { UserRole, AgeGroup } from '@/types/user';
import { getRolePermissions } from '@/utils/rolePermissions';
import InternalUsernameField from './InternalUsernameField';
import InternalPasswordSection from './InternalPasswordSection';
import InternalPinSection from './InternalPinSection';
import AgeGroupSelector from './AgeGroupSelector';
import RoleSelector from './RoleSelector';
import ReferralCodeInput from './ReferralCodeInput';
import Under13RegistrationFlow from './Under13RegistrationFlow';
import PlatformDisclaimerCheckbox from './PlatformDisclaimerCheckbox';
import { AlertTriangle } from 'lucide-react';

interface InternalRegistrationFormProps {
  onSpecialRequest: () => void;
}

const InternalRegistrationForm = ({ onSpecialRequest }: InternalRegistrationFormProps) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { register, generateSeedPhrase, loading } = useInternalAuth();
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<UserRole>('player');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<AgeGroup>('18-plus');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    pin: '',
    confirmPin: ''
  });
  const [referralCode, setReferralCode] = useState('');
  const [isReferralValid, setIsReferralValid] = useState(false);
  const [error, setError] = useState('');
  const [showAgeRestrictionWarning, setShowAgeRestrictionWarning] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  const passwordRequirements = validatePassword(formData.password);
  const isPasswordStrong = isPasswordValid(passwordRequirements);

  // Set initial values from URL params or location state
  useEffect(() => {
    // Priority: URL param > location.state
    const urlRole = searchParams.get('role');
    if (urlRole) {
      setSelectedRole(urlRole as UserRole);
      setSelectedAgeGroup('18-plus');
    } else if (location.state) {
      const { role } = location.state;
      if (role) {
        setSelectedRole(role as UserRole);
      }
      setSelectedAgeGroup('18-plus');
    }
  }, [location.state, searchParams]);

  // Check if role is available for selected age group
  useEffect(() => {
    const permissions = getRolePermissions(selectedRole, selectedAgeGroup);
    setShowAgeRestrictionWarning(!permissions.available);
  }, [selectedRole, selectedAgeGroup]);

  const handleAgeGroupChange = (value: string) => {
    const ageGroup = value as AgeGroup;
    setSelectedAgeGroup(ageGroup);
  };

  const handleRoleChange = (role: UserRole) => {
    setSelectedRole(role);
  };

  const handleReferralCodeChange = (code: string, isValid: boolean) => {
    setReferralCode(code);
    setIsReferralValid(isValid);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Check if role is available for age group
    const permissions = getRolePermissions(selectedRole, selectedAgeGroup);
    if (!permissions.available) {
      setError('This role is not available for your selected age group');
      return;
    }

    // Validation
    if (!formData.username || !formData.password || !formData.pin) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.username.length < 3) {
      setError('Username must be at least 3 characters long');
      return;
    }

    if (!isPasswordStrong) {
      setError('Password does not meet all requirements');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.pin.length < 4) {
      setError('PIN must be at least 4 characters long');
      return;
    }

    if (formData.pin !== formData.confirmPin) {
      setError('PINs do not match');
      return;
    }

    // If referral code is provided but invalid, show error
    if (referralCode && !isReferralValid) {
      setError('Please enter a valid referral code or leave it empty');
      return;
    }

    // Generate seed phrase at registration time
    const seedPhraseArray = await generateSeedPhrase();

    if (seedPhraseArray.length !== 12) {
      setError('Failed to generate recovery phrase');
      return;
    }

    const result = await register({
      username: formData.username,
      password: formData.password,
      pin: formData.pin,
      seedPhrase: seedPhraseArray.join(' '),
      role: selectedRole,
      ageGroup: selectedAgeGroup,
      referralCode: referralCode || undefined // Include referral code if provided
    });

    if (result.success) {
      // Navigate to success page with all credentials including password and PIN
      navigate('/internal-registration-success', {
        state: {
          username: formData.username,
          password: formData.password,
          pin: formData.pin,
          seedPhrase: seedPhraseArray,
          role: selectedRole,
          ageGroup: selectedAgeGroup,
          registrationDate: new Date().toISOString(),
          referralCode: referralCode || undefined
        }
      });
    } else {
      setError(result.error || 'Registration failed');
    }
  };

  const handleUnder13Registration = async (under13FormData: any) => {
    setError('');

    // Generate seed phrase
    const seedPhraseArray = await generateSeedPhrase();

    if (seedPhraseArray.length !== 12) {
      setError('Failed to generate recovery phrase');
      return;
    }

    const result = await register({
      username: under13FormData.username,
      password: under13FormData.password,
      pin: under13FormData.pin,
      seedPhrase: seedPhraseArray.join(' '),
      role: under13FormData.role,
      ageGroup: under13FormData.ageGroup,
      referralCode: under13FormData.referralCode
    });

    if (result.success) {
      // Navigate to success page with all credentials
      navigate('/internal-registration-success', {
        state: {
          username: under13FormData.username,
          password: under13FormData.password,
          pin: under13FormData.pin,
          seedPhrase: seedPhraseArray,
          role: under13FormData.role,
          ageGroup: under13FormData.ageGroup,
          registrationDate: new Date().toISOString(),
          referralCode: under13FormData.referralCode,
          teamInfo: under13FormData.teamInfo
        }
      });
    } else {
      setError(result.error || 'Registration failed');
    }
  };

  const getRoleDisplayName = (role: UserRole): string => {
    const roleNames = {
      'player': 'Player',
      'coach': 'Coach',
      'team-owner': 'Team Owner',
      'venue': 'Venue Manager',
      'fan': 'Fan',
      'parent': 'Parent',
      'child': 'Child',
      'creative': 'Creative',
      'support': 'Support'
    };
    return roleNames[role] || role;
  };

  // If 13 and under age group is selected, use special flow
  if (selectedAgeGroup === '13-and-under' && !showAgeRestrictionWarning) {
    return (
      <div className="space-y-4">
        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        <AgeGroupSelector 
          selectedAgeGroup={selectedAgeGroup}
          handleAgeGroupChange={handleAgeGroupChange}
        />

        <RoleSelector 
          selectedRole={selectedRole}
          selectedAgeGroup={selectedAgeGroup}
          setSelectedRole={handleRoleChange}
          prefilledSupportRole={location.state?.supportRole}
          prefilledRole={(searchParams.get('role') as UserRole) || (location.state?.role as UserRole)}
        />

        <Under13RegistrationFlow
          selectedRole={selectedRole}
          onRegistrationComplete={handleUnder13Registration}
          loading={loading}
          error={error}
        />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}

      <AgeGroupSelector 
        selectedAgeGroup={selectedAgeGroup}
        handleAgeGroupChange={handleAgeGroupChange}
      />

      <RoleSelector 
        selectedRole={selectedRole}
        selectedAgeGroup={selectedAgeGroup}
        setSelectedRole={handleRoleChange}
        prefilledSupportRole={location.state?.supportRole}
        prefilledRole={(searchParams.get('role') as UserRole) || (location.state?.role as UserRole)}
      />

      {showAgeRestrictionWarning && (
        <Alert className="border-red-500 bg-red-900/50">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-red-200">
            <div className="space-y-2">
              <p className="font-medium">
                The {getRoleDisplayName(selectedRole)} role is not available for the {selectedAgeGroup} age group.
              </p>
              <p className="text-sm">
                If you would like to request access, please contact the admin and fill out the special request form.
              </p>
              <Button
                type="button"
                onClick={onSpecialRequest}
                className="mt-2 bg-red-600 hover:bg-red-700 text-white"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Fill Special Request Form
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {!showAgeRestrictionWarning && (
        <>
          <InternalUsernameField
            value={formData.username}
            onChange={(username) => setFormData({ ...formData, username })}
          />

          <InternalPasswordSection
            password={formData.password}
            confirmPassword={formData.confirmPassword}
            onPasswordChange={(password) => setFormData({ ...formData, password })}
            onConfirmPasswordChange={(confirmPassword) => setFormData({ ...formData, confirmPassword })}
          />

          <InternalPinSection
            pin={formData.pin}
            confirmPin={formData.confirmPin}
            onPinChange={(pin) => setFormData({ ...formData, pin })}
            onConfirmPinChange={(confirmPin) => setFormData({ ...formData, confirmPin })}
          />

           <ReferralCodeInput onReferralCodeChange={handleReferralCodeChange} />

           <PlatformDisclaimerCheckbox
             accepted={disclaimerAccepted}
             onAcceptedChange={setDisclaimerAccepted}
           />

           <Button
             type="submit"
             disabled={loading || !isPasswordStrong || !disclaimerAccepted}
             className="w-full bg-sports-green text-black hover:opacity-90"
           >
             {loading ? 'Creating Account...' : `Sign Up as ${getRoleDisplayName(selectedRole)}`}
           </Button>
        </>
      )}
    </form>
  );
};

export default InternalRegistrationForm;
